"use client"

import * as React from "react"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
 import { cn } from "@/lib/utils"
import { cva, type VariantProps } from "class-variance-authority"
import { Icon } from "./icon"

const radioGroupVariants = cva(
  "peer aspect-square rounded-full border bg-white transition-all focus:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default: [
          "border-border hover:border-text-secondary/50",
          "text-primary",
          "data-[state=checked]:border-primary data-[state=checked]:text-primary",
        ],
        primary: [
          "border-border hover:border-primary/50",
          "text-primary",
          "data-[state=checked]:border-primary data-[state=checked]:text-primary",
        ],
        secondary: [
          "border-border hover:border-secondary/50",
          "text-secondary",
          "data-[state=checked]:border-secondary data-[state=checked]:text-secondary",
        ],
        info: [
          "border-border hover:border-info/50",
          "text-info",
          "data-[state=checked]:border-info data-[state=checked]:text-info",
        ],
        success: [
          "border-border hover:border-success/50",
          "text-success",
          "data-[state=checked]:border-success data-[state=checked]:text-success",
        ],
        warning: [
          "border-border hover:border-warning/50",
          "text-warning",
          "data-[state=checked]:border-warning data-[state=checked]:text-warning",
        ],
        error: [
          "border-border hover:border-error/50",
          "text-error",
          "data-[state=checked]:border-error data-[state=checked]:text-error",
        ],
      },
      size: {
        default: "h-4 w-4",
        sm: "h-3 w-3",
        lg: "h-5 w-5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn("grid gap-2", className)}
      {...props}
      ref={ref}
    />
  )
})
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName

interface RadioGroupItemProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>,
    VariantProps<typeof radioGroupVariants> {}

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, variant, size, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(radioGroupVariants({ variant, size, className }))}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <Icon icon="material-symbols:circle" className={cn(
          "fill-current text-current",
          size === "sm" && "h-1.5 w-1.5",
          size === "default" && "h-2 w-2",
          size === "lg" && "h-2.5 w-2.5"
        )} />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  )
})
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName

export { RadioGroup, RadioGroupItem }
